import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewContainerRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  filter,
  of,
  startWith,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import { RetailProduct } from 'src/app/models/retail-product';
import { extractQuantityAndQuery } from 'src/app/shared/helpers/extractQuantity';
import { RetailProductService } from 'src/app/shared/services/retail-product.service';
import { EditPartDialogComponent } from '../edit-part-dialog/edit-part-dialog.component';
import { TemplatePart } from '../parts-list.component';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete
} from '@angular/material/autocomplete';
import { TestIdDirective } from '../../../../../shared/directives/test-id.directive';
import { MatOption } from '@angular/material/core';
import { NgFor, AsyncPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';

@Component({
  selector: 'app-parts-search',
  templateUrl: './parts-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        width: 100%;
        padding-top: 1em;
      }
    `
  ],
  standalone: true,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    NgFor,
    MatOption,
    TestIdDirective,
    AsyncPipe
  ]
})
export class PartsSearchComponent implements OnDestroy {
  searchInput = new FormControl<string>('');
  searchResults: Observable<RetailProduct[]>;
  private ngUnsubscribe = new Subject<void>();
  FREE_TEXT_ITEM = {};

  @Output() partAdded = new EventEmitter<TemplatePart>();

  constructor(
    private retailProductService: RetailProductService,
    private dialog: MatDialog,
    private vcr: ViewContainerRef
  ) {
    this.searchResults = this.searchInput.valueChanges.pipe(
      debounceTime(300),
      switchMap((query) =>
        query
          ? this.retailProductService
              .search(encodeURIComponent(query))
              .pipe(catchError(() => of([])))
          : of([])
      ),
      startWith([])
    );
  }

  attemptQuickAdd() {
    const search = this.searchInput.value;
    this.searchInput.setValue('');
    if (typeof search !== 'string' || search === '') {
      return;
    }
    const { quantity, query } = extractQuantityAndQuery(search);
    let searchTerm = '';
    const quickAddArgs = { quantity };
    if (query && query.indexOf('b:') === 0) {
      searchTerm = query.replace('b:', '');
    } else {
      searchTerm = query;
    }
    this.retailProductService
      .search(encodeURIComponent(searchTerm))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((results) => {
        if (results.length === 1) {
          this.add(results[0], { quantity });
        }
      });
  }

  add(item: RetailProduct, quickAddArgs?: { quantity: number }) {
    this.partAdded.emit({
      name: item.name,
      price: item.retail_price,
      quantity: quickAddArgs?.quantity ?? 1,
      retail_product_id: item.id
    });
  }

  addFreeTextItem() {
    this.dialog
      .open(EditPartDialogComponent, {
        data: {},
        viewContainerRef: this.vcr
      })
      .afterClosed()
      .pipe(filter(Boolean), takeUntil(this.ngUnsubscribe), take(1))
      .subscribe(({ price, ...rest }: TemplatePart) => {
        this.partAdded.emit({ price: price * 100, ...rest });
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleOptionSelected({ option: { value } }: MatAutocompleteSelectedEvent) {
    console.log('value', value);
    if (value) {
      if (value === this.FREE_TEXT_ITEM) {
        this.addFreeTextItem();
        return;
      }
      this.add(value);
    }
  }

  emptyDisplay() {
    return '';
  }
}
